import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, Thermometer, Search, Wrench } from 'lucide-react';

const HomePage = () => {
  const badges = [
    'R134a & R1234yf Certified',
    'Leak Detection Experts',
    'No Hidden Costs',
  ];

  const services = [
    {
      icon: Thermometer,
      title: 'Air-Con Regas',
      description: 'R134a and R1234yf refrigerant recharge for optimal cooling performance.',
      link: '/regas',
    },
    {
      icon: Search,
      title: 'Diagnostics & Leak Testing',
      description: 'Electronic leak detection and pressure testing to identify faults.',
      link: '/diagnostics',
    },
    {
      icon: Wrench,
      title: 'Fault Repair',
      description: 'Component replacement and system repairs by experienced technicians.',
      link: '/problems',
    },
  ];

  const features = [
    'Proper diagnostic equipment',
    'Honest advice before work is authorised',
    'Environmentally responsible refrigerant handling',
    'Experienced technicians',
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-slate-100 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 mb-6">
                {badges.map((badge) => (
                  <span
                    key={badge}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full text-sm text-slate-700 shadow-sm"
                  >
                    <CheckCircle className="w-4 h-4 text-teal-600" />
                    {badge}
                  </span>
                ))}
              </div>

              <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-4">
                Professional Vehicle Air Conditioning Service & Repair
              </h1>
              
              <p className="text-xl text-teal-700 font-medium mb-4">
                Eastbourne & East Sussex
              </p>
              
              <p className="text-lg text-slate-600 mb-2">
                Regas • Diagnostics • Leak Testing • Fault Repair
              </p>
              
              <p className="text-slate-600 mb-8">
                Honest advice. Proper equipment. No guesswork.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors"
                  data-testid="hero-cta-primary"
                >
                  Book an Air-Con Check
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_eastbourne-cooling/artifacts/xwktik28_996D4A21-AD66-44E4-9BEC-C51937CD9820.png"
                alt="Professional technician servicing vehicle air conditioning"
                className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-teal-600 font-medium text-sm uppercase tracking-wider">Our Approach</span>
            <p className="mt-3 text-lg text-slate-600">
              Vehicle air-conditioning systems are complex and environmentally regulated. We do not "top up and hope". Every system is checked before work is recommended, so faults are identified properly and repairs last.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-3">Our Services</h2>
          <p className="text-slate-600 mb-10">
            Comprehensive air-conditioning services for all vehicle makes and models.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {services.map((service) => (
              <Link
                key={service.title}
                to={service.link}
                className="group p-6 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                data-testid={`service-card-${service.link.replace('/', '')}`}
              >
                <service.icon className="w-10 h-10 text-teal-600 mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-teal-600 transition-colors">
                  {service.title}
                </h3>
                <p className="text-slate-600 mb-4">{service.description}</p>
                <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm">
                  Learn more
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Diagnose First Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_eastbourne-cooling/artifacts/c5j83q09_Image.jpeg"
                alt="Clean modern vehicle workshop"
                className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
              />
            </div>
            <div>
              <span className="text-teal-600 font-medium text-sm uppercase tracking-wider">
                Environmentally Responsible
              </span>
              <h2 className="text-3xl font-bold text-slate-900 mt-3 mb-6">
                Diagnose First. Repair Properly.
              </h2>
              <p className="text-slate-600 mb-6">
                We treat air-conditioning faults like any other vehicle system. Our approach ensures accurate diagnosis before any work is carried out, saving you time and money.
              </p>
              <ul className="space-y-3 mb-8">
                {features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/why-us"
                className="inline-flex items-center gap-2 text-teal-600 font-medium hover:text-teal-700"
                data-testid="why-us-link"
              >
                Why Choose Us
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Air-Con Not Working?</h2>
          <p className="text-slate-300 mb-8">
            Get in touch for honest advice and a proper diagnosis.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors"
              data-testid="cta-request-appointment"
            >
              Request an Appointment
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="tel:01323643333"
              className="inline-flex items-center gap-2 bg-white text-slate-800 px-6 py-3 rounded-lg font-medium hover:bg-slate-100 transition-colors"
            >
              <Phone className="w-4 h-4" />
              01323 643333
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
